import React, { useEffect, useMemo, useState } from "react";
import { Facilities } from "@app/shared/src/api/api.gen";
import { DetectionZone, Device, Facility, FleetDefaults } from "@app/shared/src/models";
import { Card, CardHead, CardNote, FRow, FVal, Spinner, Tag, TextInput, useToast } from "@app/shared/src/components_v2";
import {
    MAX_ZONE_LENGTH_M,
    MAX_ZONE_WIDTH_M,
    MIN_ZONE_DIM_M,
    zoneLengthOutOfRange,
    zoneWidthOutOfRange,
} from "@app/shared/src/helpers/sidePassGeometry";
import { findProtectedZone, protectedZoneSize, setProtectedZoneSize } from "../../helpers/alertProvisioning";
import { SaveBar } from "../shared/SaveBar";

/**
 * The protected zone's footprint for one truck — the TMA width and length the collision alert
 * defends — edited here rather than shown read-only.
 *
 * The fleet's stated footprint is deliberately only a starting size: attenuators differ, and the
 * number that matters is the one measured on this truck. So this card is not an inherited section
 * with an override veil. It shows the fleet's figure beside the truck's own and flags a difference,
 * but a difference here is information, not drift to be reset — which is also why changing the
 * fleet default never pushes a new size to trucks that already have a zone.
 *
 * Sizes are shown in feet and stored in metres; the draft holds strings and converts on save.
 */

const M_TO_FT = 3.28084;
const ftFixed = (metres: number) => (metres * M_TO_FT).toFixed(1);
const ft = (metres: number | undefined) => (metres == null ? "—" : `${ftFixed(metres)} ft`);
const ftRange = (minM: number, maxM: number) => `${ftFixed(minM)}–${ftFixed(maxM)} ft`;

type Draft = { widthFt: string; lengthFt: string };

/** Sizes are edited to a tenth of a foot, so that is the precision two of them are compared at. */
const sameTenthFt = (a: number | undefined, b: number | undefined) =>
    a != null && b != null && ftFixed(a) === ftFixed(b);

export const ProtectedZoneSection: React.FC<{
    device: Device;
    /** The zone the collision trigger watches, when the triggers have loaded — the one to edit. */
    collisionZone?: DetectionZone;
    defaults?: FleetDefaults;
    onSaved: () => void;
}> = ({ device, collisionZone, defaults, onSaved }) => {
    const toast = useToast();
    const [facility, setFacility] = useState<Facility>();
    const [loadFailed, setLoadFailed] = useState(false);
    const [busy, setBusy] = useState(false);

    // The zone is written through the facility, so its current copy has to be in hand first.
    useEffect(() => {
        let cancelled = false;
        if (!device.facilityId) return;
        setLoadFailed(false);
        Facilities.getById(device.facilityId)
            .then((f) => !cancelled && setFacility(f))
            .catch(() => !cancelled && setLoadFailed(true));
        return () => {
            cancelled = true;
        };
    }, [device.facilityId]);

    /**
     * Prefer the zone the collision trigger actually watches; fall back to the site's one
     * non-side-pass zone for a truck whose trigger has not been set up yet.
     */
    const zone = useMemo(() => {
        if (!facility) return undefined;
        const zones = (facility.detectionZones ?? []) as DetectionZone[];
        return (collisionZone?.id && zones.find((z) => z.id === collisionZone.id)) || findProtectedZone(facility);
    }, [facility, collisionZone?.id]);

    const size = protectedZoneSize(zone);

    // A truck with no zone yet starts from the fleet's figure — the size it would be given anyway.
    const baseline = useMemo<Draft>(
        () => ({
            widthFt: size ? ftFixed(size.width) : defaults ? ftFixed(defaults.tmaWidthMetres) : "",
            lengthFt: size ? ftFixed(size.length) : defaults ? ftFixed(defaults.tmaLengthMetres) : "",
        }),
        [size?.width, size?.length, defaults?.tmaWidthMetres, defaults?.tmaLengthMetres], // eslint-disable-line react-hooks/exhaustive-deps
    );
    /**
     * Re-seed during render, not in an effect: two loads land here (the facility, then the collision
     * trigger's zone) and an effect applied each a render late, overwriting anything typed between.
     * Only untouched fields are re-seeded.
     */
    const [draft, setDraft] = useState<Draft>(baseline);
    const [seededFrom, setSeededFrom] = useState<Draft>(baseline);
    if (seededFrom !== baseline) {
        const untouched = draft.widthFt === seededFrom.widthFt && draft.lengthFt === seededFrom.lengthFt;
        setSeededFrom(baseline);
        if (untouched) setDraft(baseline);
    }

    const dirty = draft.widthFt !== baseline.widthFt || draft.lengthFt !== baseline.lengthFt;
    const widthM = Number(draft.widthFt) / M_TO_FT;
    const lengthM = Number(draft.lengthFt) / M_TO_FT;
    const valid =
        Number.isFinite(widthM) && Number.isFinite(lengthM) && !zoneWidthOutOfRange(widthM) && !zoneLengthOutOfRange(lengthM);

    const differsFromFleet =
        size !== undefined &&
        defaults !== undefined &&
        !(sameTenthFt(size.width, defaults.tmaWidthMetres) && sameTenthFt(size.length, defaults.tmaLengthMetres));

    const save = async () => {
        if (!facility || !valid) return;
        setBusy(true);
        try {
            const result = await setProtectedZoneSize(facility, widthM, lengthM);
            if (!result.ok) {
                toast(
                    result.reason === "out-of-range"
                        ? `The zone must be ${ftRange(MIN_ZONE_DIM_M, MAX_ZONE_WIDTH_M)} wide and ${ftRange(MIN_ZONE_DIM_M, MAX_ZONE_LENGTH_M)} long.`
                        : `${device.name}'s protected zone could not be saved — try again.`,
                );
                return;
            }
            // The helper updates the facility in place and hands the same object back; React would
            // bail out on the identical reference and the card would keep showing the old zone —
            // or, for a truck that had none, no zone at all.
            setFacility(Object.assign(Object.create(Object.getPrototypeOf(result.facility)), result.facility));
            const refit =
                result.refitted.length > 0
                    ? ` The ${result.refitted.join(" and ")}-side pass zone${result.refitted.length === 1 ? " was" : "s were"} re-fitted to match.`
                    : "";
            toast((result.created ? `Created ${device.name}'s protected zone.` : `${device.name}'s protected zone saved.`) + refit);
            onSaved();
        } catch {
            toast(`${device.name}'s protected zone could not be saved — try again.`);
        } finally {
            setBusy(false);
        }
    };

    const fleetHint = (metres: number | undefined) => (metres == null ? undefined : `fleet default ${ft(metres)}`);

    return (
        <Card>
            <CardHead title="Protected zone">
                {differsFromFleet && <Tag tone="mute">Differs from fleet</Tag>}
            </CardHead>
            {!facility && !loadFailed ? (
                <div className="grid place-items-center p-8 text-[var(--muted)]">
                    <Spinner />
                </div>
            ) : (
                <div className="mt-2">
                    <FRow title="Shape" sub="Area the impending-collision alert defends">
                        <FVal mu>{zone?.name ?? collisionZone?.name ?? "TMA footprint"}</FVal>
                    </FRow>
                    <FRow
                        title="TMA width"
                        sub={["Across the back of the truck", fleetHint(defaults?.tmaWidthMetres), ftRange(MIN_ZONE_DIM_M, MAX_ZONE_WIDTH_M)]
                            .filter(Boolean)
                            .join(" · ")}
                    >
                        {facility ? (
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.widthFt}
                                    onChange={(e) => setDraft({ ...draft, widthFt: e.target.value })}
                                    inputMode="decimal"
                                    className="w-[92px] text-right"
                                    aria-label="TMA width in feet"
                                    disabled={busy}
                                />
                                <span className="text-[13px] text-[var(--muted)]">ft</span>
                            </span>
                        ) : (
                            <FVal mu>{ft(protectedZoneSize(collisionZone)?.width)}</FVal>
                        )}
                    </FRow>
                    <FRow
                        title="TMA length"
                        sub={[
                            "How far the protected area reaches behind the unit",
                            fleetHint(defaults?.tmaLengthMetres),
                            ftRange(MIN_ZONE_DIM_M, MAX_ZONE_LENGTH_M),
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                    >
                        {facility ? (
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.lengthFt}
                                    onChange={(e) => setDraft({ ...draft, lengthFt: e.target.value })}
                                    inputMode="decimal"
                                    className="w-[92px] text-right"
                                    aria-label="TMA length in feet"
                                    disabled={busy}
                                />
                                <span className="text-[13px] text-[var(--muted)]">ft</span>
                            </span>
                        ) : (
                            <FVal mu>{ft(protectedZoneSize(collisionZone)?.length)}</FVal>
                        )}
                    </FRow>
                    <FRow title="Radar coverage">
                        <FVal mu>100°</FVal>
                    </FRow>
                </div>
            )}
            <CardNote>
                {loadFailed ? (
                    <>Could not load this unit's site, so its zone cannot be edited right now — reload to try again.</>
                ) : (
                    <>
                        The footprint this truck actually carries. The fleet default under Settings is only the size a new unit
                        starts at — measure the attenuator and set it here. Side-pass zones are re-fitted to the new edges
                        when the size changes.
                    </>
                )}
            </CardNote>

            <SaveBar show={dirty} busy={busy} canSave={valid && Boolean(facility)} onSave={save} onDiscard={() => setDraft(baseline)} />
        </Card>
    );
};
